import { EventEmitter } from "@angular/core"
import { Subscription, Subject, merge, Observable, of } from "rxjs"
import { debounceTime, switchMap, tap } from "rxjs/operators"
import { Asset, Device, EntityId, EntityType } from "src/app/models/entity.model"
import { ITelemetryValue } from "src/app/models/telemetry.model"
import { EntityModel, FarmerService } from "src/app/services/device.service"
import { HUMIDITY_SENSOR, IBuildingSettings, ISensorType, MOISTURE_SENSOR, TEMPERATURE_SENSOR } from "src/app/services/types.service"
import { clone, Index, shareWithGrace } from "src/app/util"
import { FarmerCellularSpear } from "../cellular-spear/spear.model"

interface IRelation<T extends EntityType> {
  to: EntityId<T>
}
export interface IBuildingState {
  settings: IBuildingSettings, devices: Device[]
}

export class FarmerBuilding {
  //updated = new EventEmitter<this>()
  updated = new Subject()
  //onLoaded = new EventEmitter<this>()
  devices: Device[] = []
  state = 'INITIAL'
  model: EntityModel<EntityType.ASSET, IBuildingSettings>
  hasDevices = null
  loaded = false

  get settings () { return this.model.settings }
  get width () { return this.settings.width || 200 }
  get length () { return this.settings.length || 300 }

  get label () { return this.asset.label }
  get entity () { return this.asset }

  // the building itself: its attributes and aggregated telemetry
  private data$: Observable<any>
  // all devices in the building (model + sensors), only for pages that show per-device values
  private devices$: Observable<any>
  private devicesChanged = new Subject<void>()

  constructor (private farmer: FarmerService, public asset: Asset) {
    this.model = this.farmer.createModel(this.farmer.types.building, this.asset)
    this.farmer.subscribe(() => {
      this.load()
    })

    this.data$ = merge(
      this.model.load().pipe(
        tap(e => this.onModelUpdate(e))
      ),

      this.updated.pipe(
        debounceTime(500)
      )
    ).pipe(
      shareWithGrace()
    )

    this.devices$ = merge(of(null), this.devicesChanged).pipe(
      switchMap(() => {
        let spears = this.getCellularSpears()
        if (spears.length == 0) return of(null)
        return merge(...spears.map(spear => spear.data$))
      }),
      tap(() => this.onDeviceLoaded()),
      shareWithGrace()
    )
  }

  latestTemperatureTs: number | null = null
  latestMoistureTs: number | null = null

  /*$devices = this.farmer.onRefresh.pipe(
    switchMap(x => {
      return this.farmer.getBuildingDevices(this.asset).filter(
        d => d.type == agTypes.farmerDeviceTypes.cellularSensorSpear.value
      ).map(x => this.farmer.getCellularSpear(x).model.$settings)
    })
  )*/
  
  // TODO: this could fail if building has other relations than cellular_spear
  getCellularSpears () {
    return this.devices.map(x => this.farmer.getCellularSpear(x))
  }
  //hasDevices () { return this.devices.length > 0 }
  hasTemperature () {
    return true
    //return this.getCellularSpears().find(x => x.hasTemperature()) != undefined
  }
  hasMoisture () {
    return true
    //return this.getCellularSpears().find(x => x.hasMoisture()) != undefined
  }

  getSensorTypes () {
    let types: ISensorType[] = []
    if (this.hasTemperature()) types.push(TEMPERATURE_SENSOR)    
    if (this.hasMoisture()) {
      if (this.settings.crop_type) {
        types.push(MOISTURE_SENSOR)
      } else {
        types.push(HUMIDITY_SENSOR)
      }
    }
    return types
  }
  
  getValues () { 
    return this.model.getValues([
      'current_min_temperature', 'current_avg_temperature', 'current_max_temperature',
      'current_min_emc', 'current_avg_emc', 'current_max_emc'
    ]) 
  }

  set_entity (entity: Asset) {
    this.asset = entity
  }

  

  // devices: also keep all devices of the building live (needed for per-device values, e.g. the sensor map)
  subscribe (cb: () => void, opt: {devices?: boolean} = {}): Subscription {
    let source = opt.devices ? merge(this.data$, this.devices$) : this.data$
    return source.subscribe(() => {
      cb()
    })
  }

  temperatureKeys () {
    let keys = ['current_min_temperature', 'current_avg_temperature', 'current_max_temperature']
    return keys.map(k => this.model.attributes.get(k))
  }
  moistureKeys () {
    let keys = ['current_min_emc', 'current_avg_emc', 'current_max_emc']
    return keys.map(k => this.model.attributes.get(k))
  }

  isValueValid (value?: ITelemetryValue<number>) {
    if (value == undefined || value.status == 'error') return false;
    let now = (new Date().getTime() + new Date().getTimezoneOffset()*60*1000);
    let maxAge = now - (24 * 60 * 60 * 1000) // one day
    return value != undefined && value.value != undefined && typeof value.value == 'number' 
      && value.value > -99 && value.value < 99 && value.ts > maxAge
  }

  createState (): IBuildingState {
    return {settings: clone(this.settings), devices: this.devices.slice(0)}
  }

  get telemetry () { return this.farmer.telemetry }
  get http () { return this.farmer.http }
  get attributeService () { return this.farmer.attributeService }
  get entityService () { return this.farmer.entityService }

  emit () {
    this.updated.next(this)
  }

  onModelUpdate (settings: Partial<IBuildingSettings>) {
    this.latestTemperatureTs = this.model.getLatestTs([
      'current_min_temperature', 'current_avg_temperature', 'current_max_temperature'
    ])
    this.latestMoistureTs = this.model.getLatestTs([
      'current_min_emc', 'current_avg_emc', 'current_max_emc'
    ])
    this.emit()
  }

  async saveSettings (settings: Partial<IBuildingSettings>) {
    await this.model.saveSettings(settings)
    this.devices.map(device => {
      this.farmer.getCellularSpear(device).onBuildingUpdated()
    })
    this.emit()
  }

  isLoaded () { return this.state == 'LOADED' }
  hasFailed () {
    return this.model.loading.is_failed() || this.getCellularSpears().find(x => x.model.loading.is_failed()) != undefined
  }

  // start over after websocket subscriptions gave up retrying
  retry () {
    this.model.retry()
    this.getCellularSpears().forEach(spear => spear.retry())
  }

  load () {
    let previousIds = this.devices.map(d => d.id.id).sort().join(',')
    this.devices = []
    let devices = this.farmer.getBuildingDevices(this.asset)
    this.hasDevices = devices.length > 0
    for (var device of devices) {
      this.addDevice(device)
    }
    this.onDeviceLoaded()
    if (previousIds != devices.map(d => d.id.id).sort().join(',')) {
      this.devicesChanged.next()
    }
  }

  onDeviceLoaded () {
    // before the farmer service has loaded the relations the device list is always empty,
    // which would show the building as loaded without devices for a moment
    if (!this.farmer.loaded) return
    let allLoaded = this.getCellularSpears().find(x => !x.isLoaded()) == undefined
    if (allLoaded) {
      this.loaded = true
      //this.onLoaded.emit(this)
      this.emit()
    }
  }

  async getDeviceRelations () {
    let url = `/api/relations/info?fromId=${this.asset.id.id}&fromType=ASSET`
    return await this.http.get<IRelation<EntityType.DEVICE>[]>(url).toPromise()
  }

  async createRelationTo (device: Device) {
    let url = '/api/relation'
    await this.http.post(url, {
      additionalInfo: "",
      from: this.asset.id,
      to: device.id,
      type: 'Contains',
      typeGroup: "COMMON"
    }).toPromise()
  }
  async removeRelationTo (entityId: EntityId<EntityType.DEVICE>) {
    let url = '/api/relation?relationTypeGroup=COMMON'
    url += '&fromId=' + this.asset.id.id
    url += '&fromType=' + this.asset.id.entityType 
    url += '&relationType=Contains'
    url += '&toId=' + entityId.id 
    url += '&toType=' + entityId.entityType
    await this.http.delete(url).toPromise()
  }

  async assignDevice (device: Device) {
    let currentDevice = this.devices.find(x => x.id.id == device.id.id)
    if (!currentDevice) {
      await this.createRelationTo(device)
      this.devices.push(device)
    }
    await this.farmer.reload()
    this.emit()
  }
  async unassignDevice (device: Device) {
    let hasDevice = this.devices.find(x => x.id.id == device.id.id)
    if (hasDevice) {
      await this.removeRelationTo(device.id)
      await this.farmer.reload()
      this.emit()
    }
  }

  async saveMarkers (markers: IBuildingMarker[]) {
    // TODO: create relation operations should run in farmer-service
    let newDevices: Device[] = []
    for (let marker of markers) {
      let device = this.devices.find(x => x.id.id == marker.device.id.id)
      if (!device) {
        await this.createRelationTo(marker.device)
        device = marker.device
      }
      newDevices.push(device)
      let ctrl = this.farmer.getCellularSpear(device)
      await ctrl.saveSettings({pos_x: marker.realPos.x, pos_y: marker.realPos.y})
    }
    for (var device of this.devices) {
      let hasDevice = newDevices.find(x => x.id.id == device.id.id) != undefined
      if (!hasDevice) {
        await this.removeRelationTo(device.id)
      }
    }
    this.devices = newDevices
    await this.farmer.reload()
    this.emit()
  }
  updateDevicePosition (device: Device, x: number, y: number) {
    this.farmer.getCellularSpear(device).saveSettings({
      pos_x: x, pos_y: y
    })
  }

  _subscriptions: Index<Subscription> = {}
  addDevice (device: Device) {
    this.devices.push(device)
    let ctrl = this.farmer.getCellularSpear(device)
    ctrl.load()
    if (this._subscriptions[device.id.id]) {
      this._subscriptions[device.id.id].unsubscribe()
    }
    if (this._subscriptions[device.id.id + '-data']) {
      this._subscriptions[device.id.id + '-data'].unsubscribe()
    }
    this._subscriptions[device.id.id] = ctrl.onUpdate.subscribe(e => {
      this.onDeviceLoaded()
      this.emit()
    })
    this._subscriptions[device.id.id + '-data'] = ctrl.onDataUpdate.subscribe(e => {
      this.emit()
    })
    this.emit()
  }
  removeDevice (device: Device) {
    if (this._subscriptions[device.id.id]) {
      this._subscriptions[device.id.id].unsubscribe()
    }
    if (this._subscriptions[device.id.id + '-data']) {
      this._subscriptions[device.id.id + '-data'].unsubscribe()
    }
    this.devices = this.devices.filter(d => d.id.id != device.id.id)
    this.emit()
  }
}


export function getTouchEvent (evt: MouseEvent | TouchEvent) {
  if (evt instanceof MouseEvent) return evt
  else return evt.touches[0]
}

// client coordinates (css pixels of the viewport), screen coordinates do not match the page under zoom
export function getEventCoords (evt: MouseEvent | TouchEvent) {
  if (evt instanceof MouseEvent) {
    return {x: evt.clientX, y: evt.clientY}
  } else {
    let touch = evt.touches[0]
    return {x: touch.clientX, y: touch.clientY}
  } 
}


export interface IBuildingMarker {
  id: string, device: Device
  realPos: {x: number, y: number}
  viewPos: {x: number, y: number}
  value: ITelemetryValue<number>
  unit: string, loaded?: boolean
  bound: {width: number, height: number}
  valid: boolean, sensor: string
}
